import { FormEvent, Fragment, useCallback, useEffect, useMemo, useState } from "react";

import {
  APIError,
  agentUpdateView,
  archiveManagedTenant,
  assignGlobalDevice,
  createGlobalDevice,
  createGlobalDeviceEnrollment,
  createManagedMeasurementPoint,
  createManagedMeasurementPointGroup,
  createManagedTenant,
  deactivateGlobalDevice,
  deleteManagedMeasurementPointGroup,
  deviceLabel,
  getAgentRelease,
  getGlobalDevices,
  getUnassignedMeasurements,
  getManagedMeasurementPointGroups,
  getManagedMeasurementPoints,
  getManagedTenants,
  isAgentReleaseNewer,
  isAgentUpdateConfirmed,
  isSupersededAgentUpdate,
  logout,
  purgeManagedTenant,
  requestGlobalDeviceUpdate,
  restartGlobalDevice,
  resolveUnassignedMeasurements,
  restoreManagedTenant,
  supportsExtensions,
  supportsRemoteRestart,
  supportsRemoteUpdates,
  unassignGlobalDevice,
  updateRequiresMaintenance,
  type AgentRelease,
  type Device,
  type DeviceEnrollment,
  type GlobalDevice,
  type Identity,
  type ManagedMeasurementPointGroup,
  type ManagedTenant,
  type MeasurementPoint,
  type UnassignedMeasurementGroup,
} from "./api";
import { displayLocale } from "./localization";
import { DeviceExtensions } from "./DeviceExtensions";
import { RemoteWebAccess } from "./RemoteWebAccess";
import { NarrowPassageOperations } from "./NarrowPassageOperations";
import { ExtensionHealth } from "./ExtensionHealth";
import { AgentSettings } from "./AgentSettings";
import { formatNetworkBytes } from "./networkUsageFormat";
import { AgentUpdateProgress } from "./AgentUpdateProgress";
import { EnrollmentCommand } from "./EnrollmentCommand";
import { InstallationProgress } from "./InstallationProgress";
import { AuthenticatedHeader } from "./Shell";
import { TenantPurgeConfirmation } from "./TenantPurgeConfirmation";
import { ExtensionAdministration } from "./ExtensionAdministration";
import { DeviceStorageCells, StorageWarnings } from "./Storage";
import { BackupAdministration } from "./BackupAdministration";
import { useNavigationHistory } from "./navigationHistory";

const emptyGroupForm = { name: "", side1: "", side2: "", timezone: "Europe/Vienna", from: "", to: "" };

function campaignDate(value: string) {
  return new Date(`${value}T00:00:00Z`).toLocaleDateString(displayLocale, { timeZone: "UTC" });
}

const platformNavigation = [{ id: "overview", label: "Overview" }, { id: "management", label: "Verwaltung" }, { id: "devices", label: "Geräte" }, { id: "extensions", label: "Extensions" }, { id: "backups", label: "Einstellungen" }];

function managementError(reason: unknown, fallback: string) {
  if (reason instanceof APIError && reason.message === "device cleanup pending") return "Das Gerät zuerst von seiner Messstelle lösen und die Bereinigung abwarten. Den Pi dafür online lassen.";
  if (reason instanceof APIError && reason.status === 409) return "Der Name oder die E-Mail-Adresse ist bereits vergeben.";
  if (reason instanceof APIError && reason.status === 400) return "Bitte prüfe die eingegebenen Werte.";
  return fallback;
}

function activityTimestamp(...values: Array<string | undefined>) {
  for (const value of values) {
    if (!value) continue;
    const timestamp = Date.parse(value);
    if (!Number.isNaN(timestamp)) return timestamp;
  }
  return 0;
}

export function PlatformManagement({ identity, onSignedOut }: { identity: Identity; onSignedOut: () => void }) {
  const [activeNavigation, setActiveNavigation] = useState<"overview" | "management" | "devices" | "extensions" | "backups">("management");
  const [tenants, setTenants] = useState<ManagedTenant[]>([]);
  const [selectedTenantID, setSelectedTenantID] = useState("");
  const [points, setPoints] = useState<MeasurementPoint[]>([]);
  const [devices, setDevices] = useState<GlobalDevice[]>([]);
  const [deviceRefreshFailed, setDeviceRefreshFailed] = useState(false);
  const [tenantForm, setTenantForm] = useState({ name: "", email: "", password: "", confirmation: "" });
  const [pointName, setPointName] = useState("");
  const [showTenantForm, setShowTenantForm] = useState(false);
  const [showPointForm, setShowPointForm] = useState(false);
  const [groups, setGroups] = useState<ManagedMeasurementPointGroup[]>([]);
  const [operationsGroupID, setOperationsGroupID] = useState("");
  const [loadedGroupsTenantID, setLoadedGroupsTenantID] = useState("");
  const [groupFormTenantID, setGroupFormTenantID] = useState("");
  const [groupForm, setGroupForm] = useState(emptyGroupForm);
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadedPointsTenantID, setLoadedPointsTenantID] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [release, setRelease] = useState<AgentRelease | null>(null);
  const [enrollment, setEnrollment] = useState<{ deviceLabel: string; deviceID: string; value: DeviceEnrollment } | null>(null);
  const [detailsDeviceID, setDetailsDeviceID] = useState("");
  const [extensionsDevice, setExtensionsDevice] = useState<Device | null>(null);
  const [agentSettingsDeviceID, setAgentSettingsDeviceID] = useState("");
  const [purgeTenant, setPurgeTenant] = useState<ManagedTenant | null>(null);
  const [unassignedGroups, setUnassignedGroups] = useState<UnassignedMeasurementGroup[]>([]);
  const [archivePoints, setArchivePoints] = useState<Array<MeasurementPoint & { tenant: string }>>([]);
  const [archiveTargets, setArchiveTargets] = useState<Record<string, string>>({});
  const [archiveLoaded, setArchiveLoaded] = useState(false);

  const handleNavigation = useCallback((id: string) => {
    setActiveNavigation(id === "overview" || id === "devices" || id === "extensions" || id === "backups" ? id : "management");
    setDetailsDeviceID(""); setExtensionsDevice(null); setAgentSettingsDeviceID(""); setEnrollment(null); setError(""); setNotice("");
  }, []);
  const navigateSection = useNavigationHistory({ scope: "platform", navigation: platformNavigation, activeNavigation, defaultNavigation: "management", onNavigate: handleNavigation });

  const selectedTenant = useMemo(() => tenants.find((tenant) => tenant.id === selectedTenantID), [tenants, selectedTenantID]);
  const pointsLoading = Boolean(selectedTenantID && loadedPointsTenantID !== selectedTenantID);
  const visiblePoints = loadedPointsTenantID === selectedTenantID ? points : [];
  const groupsLoading = Boolean(selectedTenantID && loadedGroupsTenantID !== selectedTenantID);
  const visibleGroups = loadedGroupsTenantID === selectedTenantID ? groups : [];
  const showGroupForm = Boolean(selectedTenantID) && groupFormTenantID === selectedTenantID;
  const activeDevices = useMemo(() => devices.filter((item) => item.device.active), [devices]);
  const retiredDevices = useMemo(() => devices.filter((item) => !item.device.active), [devices]);
  const deviceSummary = useMemo(() => ({
    free: devices.filter((item) => item.device.active && !item.measurement_point && !item.cleanup_pending).length,
    online: devices.filter((item) => item.device.active && item.device.status?.online).length,
  }), [devices]);
  const networkWarnings = useMemo(() => devices.filter((item) => item.device.active && item.device.network_usage?.warning), [devices]);
  const tenantSummary = useMemo(() => ({
    active: tenants.filter((tenant) => !tenant.archived_at).length,
    archived: tenants.filter((tenant) => tenant.archived_at).length,
    points: tenants.filter((tenant) => !tenant.archived_at).reduce((sum, tenant) => sum + tenant.total_device_count, 0),
  }), [tenants]);

  const handleFailure = useCallback((reason: unknown, fallback: string) => {
    if (reason instanceof APIError && reason.status === 401) {
      onSignedOut();
      return;
    }
    setError(managementError(reason, fallback));
  }, [onSignedOut]);

  const loadTenantsAndDevices = useCallback(async (preferredTenantID?: string) => {
    const [tenantResult, deviceResult] = await Promise.all([getManagedTenants(), getGlobalDevices()]);
    setTenants(tenantResult);
    setDevices(deviceResult);
    setSelectedTenantID((current) => {
      const preferred = preferredTenantID || current;
      return tenantResult.some((tenant) => tenant.id === preferred && !tenant.archived_at)
        ? preferred
        : (tenantResult.find((tenant) => !tenant.archived_at)?.id ?? "");
    });
  }, []);

  const loadPoints = useCallback(async (tenantID: string) => {
    if (!tenantID) { setPoints([]); setLoadedPointsTenantID(""); return; }
    setLoadedPointsTenantID("");
    try { setPoints(await getManagedMeasurementPoints(tenantID)); }
    finally { setLoadedPointsTenantID(tenantID); }
  }, []);

  const loadGroups = useCallback(async (tenantID: string) => {
    setLoadedGroupsTenantID("");
    try { setGroups(await getManagedMeasurementPointGroups(tenantID)); }
    finally { setLoadedGroupsTenantID(tenantID); }
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([getManagedTenants(), getGlobalDevices()])
      .then(([tenantResult, deviceResult]) => {
        if (!active) return;
        setTenants(tenantResult);
        setDevices(deviceResult);
        setSelectedTenantID(tenantResult.find((tenant) => !tenant.archived_at)?.id ?? "");
      })
      .catch((reason: unknown) => { if (active) handleFailure(reason, "Die Verwaltung konnte nicht geladen werden."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [handleFailure]);

  useEffect(() => {
    getAgentRelease()
      .then(setRelease)
      .catch((reason: unknown) => handleFailure(reason, "Die Agent-Version konnte nicht geladen werden."));
  }, [handleFailure]);

  useEffect(() => {
    if (activeNavigation !== "devices" || archiveLoaded || loading) return;
    let active = true;
    Promise.all([
      getUnassignedMeasurements(),
      Promise.all(tenants.filter((tenant) => !tenant.archived_at).map(async (tenant) =>
        (await getManagedMeasurementPoints(tenant.id)).map((point) => ({ ...point, tenant: tenant.name })))),
    ]).then(([groups, pointGroups]) => {
      if (!active) return;
      setUnassignedGroups(groups);
      setArchivePoints(pointGroups.flat());
      setArchiveLoaded(true);
    }).catch((reason: unknown) => {
      if (active) handleFailure(reason, "Nicht zugeordnete Messdaten konnten nicht geladen werden.");
    });
    return () => { active = false; };
  }, [activeNavigation, archiveLoaded, handleFailure, loading, tenants]);

  useEffect(() => {
    const refreshable = devices.some(({ installation, device, cleanup_pending }) => cleanup_pending ||
      (installation && !["succeeded", "failed"].includes(installation.state) && new Date(installation.report_expires_at).getTime() > Date.now()) ||
      (device.agent_update && !isSupersededAgentUpdate(device) && !isAgentUpdateConfirmed(device) && ["requested", "downloading", "verified", "installing", "healthy"].includes(device.agent_update.state)));
    if (loading) return;
    let active = true;
    let refreshing = false;
    const timer = window.setInterval(() => {
      if (refreshing) return;
      refreshing = true;
      void getGlobalDevices().then((result) => {
        if (active) { setDevices(result); setDeviceRefreshFailed(false); }
      }).catch(() => {
        if (active) setDeviceRefreshFailed(true);
      }).finally(() => { refreshing = false; });
    }, refreshable ? 3000 : 60000);
    return () => { active = false; window.clearInterval(timer); };
  }, [devices, loading]);

  useEffect(() => {
    let active = true;
    if (!selectedTenantID) return () => { active = false; };
    getManagedMeasurementPoints(selectedTenantID)
      .then((result) => { if (active) setPoints(result); })
      .catch((reason: unknown) => {
        if (!active) return;
        setPoints([]);
        handleFailure(reason, "Die Messstellen konnten nicht geladen werden.");
      })
      .finally(() => { if (active) setLoadedPointsTenantID(selectedTenantID); });
    return () => { active = false; };
  }, [handleFailure, selectedTenantID]);

  useEffect(() => {
    let active = true;
    if (!selectedTenantID) return () => { active = false; };
    getManagedMeasurementPointGroups(selectedTenantID)
      .then((result) => { if (active) setGroups(result); })
      .catch((reason: unknown) => {
        if (!active) return;
        setGroups([]);
        handleFailure(reason, "Die Messstellenverbünde konnten nicht geladen werden.");
      })
      .finally(() => { if (active) setLoadedGroupsTenantID(selectedTenantID); });
    return () => { active = false; };
  }, [handleFailure, selectedTenantID]);

  async function signOut() {
    try { await logout(); } finally { onSignedOut(); }
  }

  async function submitTenant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (tenantForm.password !== tenantForm.confirmation) { setError("Die beiden Passwörter stimmen nicht überein."); return; }
    setBusy("tenant"); setError(""); setNotice("");
    try {
      const created = await createManagedTenant({ name: tenantForm.name.trim(), admin_email: tenantForm.email.trim(), admin_password: tenantForm.password });
      setTenantForm({ name: "", email: "", password: "", confirmation: "" });
      setShowTenantForm(false);
      await loadTenantsAndDevices(created.id);
      setNotice(`Kunde „${created.name}“ wurde angelegt.`);
    } catch (reason) { handleFailure(reason, "Der Kunde konnte nicht angelegt werden."); }
    finally { setBusy(""); }
  }

  async function submitPoint(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedTenant) return;
    setBusy("point"); setError(""); setNotice("");
    try {
      await createManagedMeasurementPoint(selectedTenant.id, pointName.trim());
      setPointName(""); setShowPointForm(false);
      await Promise.all([loadPoints(selectedTenant.id), loadTenantsAndDevices(selectedTenant.id)]);
      setArchiveLoaded(false);
      setNotice("Die Messstelle wurde angelegt. Ihre Historie bleibt künftig unabhängig vom eingesetzten Gerät erhalten.");
    } catch (reason) { handleFailure(reason, "Die Messstelle konnte nicht angelegt werden."); }
    finally { setBusy(""); }
  }

  async function submitGroup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedTenant) return;
    if (groupForm.side1 === groupForm.side2) { setError("Seite 1 und Seite 2 brauchen zwei verschiedene Messstellen."); return; }
    if (Boolean(groupForm.from) !== Boolean(groupForm.to)) { setError("Für die Kampagne Beginn und Ende angeben oder beide leer lassen."); return; }
    const name = groupForm.name.trim();
    setBusy("group"); setError(""); setNotice("");
    try {
      await createManagedMeasurementPointGroup(selectedTenant.id, {
        name,
        profile: "narrow_passage",
        timezone: groupForm.timezone.trim(),
        campaign: groupForm.from ? { from_date: groupForm.from, to_date: groupForm.to } : null,
        members: [{ measurement_point_id: groupForm.side1, side: 1 }, { measurement_point_id: groupForm.side2, side: 2 }],
      });
      setGroupForm(emptyGroupForm); setGroupFormTenantID("");
      await loadGroups(selectedTenant.id);
      setNotice(`Der Verbund „${name}“ wurde angelegt. Die Linienrollen werden im Kundenportal unter „Messstellenverbünde“ zugeordnet.`);
    } catch (reason) {
      if (reason instanceof APIError && reason.status === 404) setError("Eine der gewählten Messstellen gibt es nicht mehr. Bitte die Seite neu laden.");
      else handleFailure(reason, "Der Verbund konnte nicht angelegt werden.");
    } finally { setBusy(""); }
  }

  async function removeGroup(group: ManagedMeasurementPointGroup) {
    if (!selectedTenant || !window.confirm(`Verbund „${group.name}“ wirklich löschen? Die Messstellen und ihre Messdaten bleiben erhalten, die Linienrollen des Verbunds entfallen.`)) return;
    setBusy(`group:${group.id}`); setError(""); setNotice("");
    try {
      await deleteManagedMeasurementPointGroup(selectedTenant.id, group.id);
      setNotice(`Der Verbund „${group.name}“ wurde gelöscht.`);
    } catch (reason) { handleFailure(reason, "Der Verbund konnte nicht gelöscht werden."); }
    finally {
      setBusy("");
      await loadGroups(selectedTenant.id).catch((reason: unknown) => handleFailure(reason, "Die Messstellenverbünde konnten nicht geladen werden."));
    }
  }

  async function addDevice() {
    setBusy("device"); setError(""); setNotice("");
    try {
      const device = await createGlobalDevice();
      let preparedEnrollment: DeviceEnrollment | null = null;
      try {
        preparedEnrollment = await createGlobalDeviceEnrollment(device.id);
      } catch (reason) {
        handleFailure(reason, "Das Gerät wurde angelegt, aber der Installationsbefehl konnte nicht erstellt werden.");
      }
      await loadTenantsAndDevices(selectedTenantID);
      setDetailsDeviceID(device.id);
      setExtensionsDevice(null);
      setAgentSettingsDeviceID("");
      setEnrollment(preparedEnrollment ? { deviceLabel: deviceLabel(device), deviceID: device.id, value: preparedEnrollment } : null);
      if (preparedEnrollment) {
        setNotice(`${deviceLabel(device)} wurde angelegt. Der Installationsbefehl ist unten bereit.`);
      }
    } catch (reason) { handleFailure(reason, "Das Gerät konnte nicht angelegt werden."); }
    finally { setBusy(""); }
  }

  async function assign(point: MeasurementPoint, deviceID: string) {
    if (!selectedTenant || !deviceID) return;
    const selected = devices.find((item) => item.device.id === deviceID);
    if (!selected) return;
    const previous = selected.measurement_point ? ` Die bisherige Zuweisung zu „${selected.measurement_point}“ wird beendet.` : "";
    if (!window.confirm(`${deviceLabel(selected.device)} der Messstelle „${point.name}“ zuweisen?${previous}`)) return;
    setBusy(`assign:${point.id}`); setError(""); setNotice("");
    try {
      await assignGlobalDevice(selectedTenant.id, point.id, deviceID);
      await Promise.all([loadPoints(selectedTenant.id), loadTenantsAndDevices(selectedTenant.id)]);
      setNotice(`${deviceLabel(selected.device)} ist jetzt „${point.name}“ zugewiesen. Frühere Messungen bleiben an ihren ursprünglichen Messstellen.`);
    } catch (reason) { handleFailure(reason, "Das Gerät konnte nicht zugewiesen werden."); }
    finally { setBusy(""); }
  }

  async function unassign(point: MeasurementPoint) {
    if (!selectedTenant || !point.assignment) return;
    const label = deviceLabel(point.assignment.device);
    if (!window.confirm(`${label} von der Messstelle „${point.name}“ lösen? Die Pi-Daten werden bereinigt. Bisherige Messungen bleiben bei dieser Messstelle; frei wird das Gerät nach seiner Bereinigungsbestätigung.`)) return;
    setBusy(`assign:${point.id}`); setError(""); setNotice("");
    try {
      await unassignGlobalDevice(selectedTenant.id, point.id);
      await Promise.all([loadPoints(selectedTenant.id), loadTenantsAndDevices(selectedTenant.id)]);
      setNotice(`${label} wurde von „${point.name}“ gelöst. Die Bereinigung läuft; das Gerät wird danach automatisch freigegeben.`);
    } catch (reason) { handleFailure(reason, "Das Gerät konnte nicht von der Messstelle gelöst werden."); }
    finally { setBusy(""); }
  }

  async function deactivate(device: GlobalDevice) {
    if (!window.confirm(`${deviceLabel(device.device)} wirklich außer Betrieb nehmen? Das Zertifikat wird gesperrt und eine aktive Zuweisung beendet.`)) return;
    setBusy(`device:${device.device.id}`); setError(""); setNotice("");
    try {
      await deactivateGlobalDevice(device.device.id);
      await Promise.all([loadTenantsAndDevices(selectedTenantID), loadPoints(selectedTenantID)]);
      setDetailsDeviceID("");
      setEnrollment(null);
      setExtensionsDevice(null);
      setAgentSettingsDeviceID("");
      setNotice(`${deviceLabel(device.device)} wurde deaktiviert.`);
    } catch (reason) { handleFailure(reason, "Das Gerät konnte nicht deaktiviert werden."); }
    finally { setBusy(""); }
  }

  async function prepareEnrollment(device: Device) {
    setBusy(`enrollment:${device.id}`); setError(""); setNotice("");
    try {
      const value = await createGlobalDeviceEnrollment(device.id);
      setDetailsDeviceID(device.id);
      setExtensionsDevice(null);
      setAgentSettingsDeviceID("");
      setEnrollment({ deviceLabel: deviceLabel(device), deviceID: device.id, value });
    } catch (reason) { handleFailure(reason, "Der Installationsbefehl konnte nicht erstellt werden."); }
    finally { setBusy(""); }
  }

  async function updateAgent(device: Device) {
    if (!release || !window.confirm(`${deviceLabel(device)} jetzt auf Agent ${release.version} aktualisieren?`)) return;
    setBusy(`update:${device.id}`); setError(""); setNotice("");
    try {
      await requestGlobalDeviceUpdate(device.id);
      await loadTenantsAndDevices(selectedTenantID);
      setEnrollment(null);
      setExtensionsDevice(null);
      setDetailsDeviceID(device.id);
      setNotice(`Update auf Agent ${release.version} wurde angefordert. Offline-Geräte führen es beim nächsten Verbindungsaufbau aus.`);
    } catch (reason) { handleFailure(reason, "Das Update konnte nicht angefordert werden."); }
    finally { setBusy(""); }
  }

  async function restartDevice(device: Device) {
    if (!window.confirm(`${deviceLabel(device)} wirklich neu starten? Der Raspberry Pi ist während des Neustarts kurz nicht erreichbar.`)) return;
    setBusy(`restart:${device.id}`); setError(""); setNotice("");
    try {
      await restartGlobalDevice(device.id);
      setNotice(`Neustart für ${deviceLabel(device)} wurde angefordert.`);
    } catch (reason) {
      if (reason instanceof APIError && reason.status === 409) setError("Das Gerät ist offline oder unterstützt den sicheren Remote-Neustart noch nicht.");
      else if (reason instanceof APIError && reason.status === 429) setError("Für dieses Gerät wurde gerade bereits ein Neustart angefordert. Bitte kurz warten.");
      else handleFailure(reason, "Der Neustart konnte nicht angefordert werden.");
    }
    finally { setBusy(""); }
  }

  async function resolveArchiveGroup(group: UnassignedMeasurementGroup) {
    const key = `${group.device_id}:${group.claimed_assignment_id}`;
    const pointID = archiveTargets[key];
    const point = archivePoints.find((item) => item.id === pointID);
    if (!point || !window.confirm(`${group.record_count.toLocaleString(displayLocale)} Messwerte von ${group.serial_number || group.device_cn} der Messstelle „${point.name}“ zuordnen?`)) return;
    setBusy(`archive:${key}`); setError(""); setNotice("");
    try {
      const result = await resolveUnassignedMeasurements(group, pointID);
      setUnassignedGroups((current) => current.filter((item) => `${item.device_id}:${item.claimed_assignment_id}` !== key));
      setNotice(`${result.resolved_records.toLocaleString(displayLocale)} Messwerte wurden „${point.name}“ zugeordnet.`);
    } catch (reason) {
      if (reason instanceof APIError && reason.status === 409) setError("Der Messzeitraum überschneidet sich mit einer bestehenden Geräte- oder Messstellenhistorie. Die Zuordnung wurde nicht verändert.");
      else handleFailure(reason, "Die Messdaten konnten nicht zugeordnet werden.");
    } finally { setBusy(""); }
  }

  async function removeTenant(tenant: ManagedTenant) {
    if (!window.confirm(`Kunde „${tenant.name}“ wirklich archivieren? Messstellen und Historie bleiben erhalten, aktive Geräte werden nur freigegeben.`)) return;
    setBusy(`archive:${tenant.id}`); setError(""); setNotice("");
    try {
      await archiveManagedTenant(tenant.id);
      await loadTenantsAndDevices();
      setNotice(`Kunde „${tenant.name}“ wurde archiviert; seine Messhistorie wurde nicht einem anderen Kunden übertragen.`);
    } catch (reason) { handleFailure(reason, "Der Kunde konnte nicht archiviert werden."); }
    finally { setBusy(""); }
  }

  async function restoreTenant(tenant: ManagedTenant) {
    setBusy(`restore:${tenant.id}`); setError(""); setNotice("");
    try {
      await restoreManagedTenant(tenant.id);
      await loadTenantsAndDevices(tenant.id);
      setNotice(`Kunde „${tenant.name}“ wurde dearchiviert und kann sich wieder anmelden.`);
    } catch (reason) {
      if (reason instanceof APIError && reason.status === 409) setError("Der Kunde ist nicht mehr archiviert. Bitte die Ansicht neu laden.");
      else handleFailure(reason, "Der Kunde konnte nicht dearchiviert werden.");
    }
    finally { setBusy(""); }
  }

  async function permanentlyDeleteTenant(tenant: ManagedTenant, confirmation: string) {
    setBusy(`purge:${tenant.id}`); setError(""); setNotice("");
    try {
      await purgeManagedTenant(tenant.id, confirmation);
      setPurgeTenant(null);
      await loadTenantsAndDevices();
      setNotice(`Kunde „${tenant.name}“ und alle zugehörigen Daten wurden vollständig gelöscht.`);
    } catch (reason) {
      if (reason instanceof APIError && reason.status === 409) setError("Der Kunde muss vor der vollständigen Löschung archiviert sein.");
      else if (reason instanceof APIError && reason.status === 400) setError("Die eingegebene Kundenkennung stimmt nicht überein.");
      else handleFailure(reason, "Der Kunde konnte nicht vollständig gelöscht werden.");
    }
    finally { setBusy(""); }
  }

  function renderDeviceRow(item: GlobalDevice) {
    const expanded = detailsDeviceID === item.device.id;
    const update = agentUpdateView(item.device, release);
    const maintenanceRequired = updateRequiresMaintenance(item.device, release);
    const installedAgent = item.device.status?.agent_version;
    const setupComplete = Boolean(installedAgent || item.installation?.state === "succeeded");
    const detailID = `device-details-${item.device.id}`;
    const enrollmentForDevice = enrollment?.deviceID === item.device.id ? enrollment : null;
    const extensionsForDevice = extensionsDevice?.id === item.device.id;
    const agentSettingsForDevice = agentSettingsDeviceID === item.device.id;
    const localAddress = item.device.status?.local_address;
    const installationTimestamp = activityTimestamp(item.installation?.finished_at, item.installation?.started_at, item.installation?.created_at);
    const updateTimestamp = activityTimestamp(item.device.agent_update?.reported_at, item.device.agent_update?.requested_at);
    const supersededUpdate = isSupersededAgentUpdate(item.device) || item.device.agent_update?.state === "superseded";
    const latestActivity = enrollmentForDevice
      ? "installation"
      : supersededUpdate
        ? "current"
        : item.installation && installationTimestamp >= updateTimestamp
          ? "installation"
          : item.device.agent_update
            ? "update"
            : null;

    function closeDetails() {
      setDetailsDeviceID("");
      setEnrollment(null);
      setExtensionsDevice(null);
      setAgentSettingsDeviceID("");
    }
    function toggleDetails() {
      if (expanded) closeDetails();
      else { setEnrollment(null); setExtensionsDevice(null); setAgentSettingsDeviceID(""); setDetailsDeviceID(item.device.id); }
    }

    return <Fragment key={item.device.id}>
      <tr className={`device-row clickable-device-row${expanded ? " selected" : ""}`} tabIndex={0} aria-expanded={expanded} aria-controls={detailID} aria-label={`${deviceLabel(item.device)} Details ${expanded ? "schließen" : "öffnen"}`} onClick={toggleDetails} onKeyDown={event => { if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); toggleDetails(); } }}>
        <td><strong>{deviceLabel(item.device)}</strong></td>
        <td>{item.measurement_point ? `${item.tenant} · ${item.measurement_point}` : item.cleanup_pending ? "Bereinigung ausstehend · Pi online lassen" : item.device.active ? "Frei" : "Keine aktive Zuweisung"}</td>
        <td className="device-status-cell">
          <span className={`state-badge ${item.device.active && item.device.status?.online ? "online" : item.device.active && item.device.status ? "warning" : "neutral"}`}><i />{!item.device.active ? "Außer Betrieb" : item.device.status?.online ? "Online" : item.device.status ? "Offline" : "Bereit"}</span>
          <small>{item.device.status?.last_seen ? `Letzter Heartbeat: ${new Date(item.device.status.last_seen).toLocaleString(displayLocale)}` : "Noch kein Heartbeat"}</small>
        </td>
        <td className="device-address-cell">
          {localAddress ? <code>{localAddress}</code> : <strong>–</strong>}
          <small>{localAddress ? item.device.status?.online ? "MQTT-Verbindung" : "Zuletzt gemeldet" : "Noch nicht gemeldet"}</small>
        </td>
        <DeviceStorageCells storage={item.device.status?.storage} online={item.device.status?.online} />
        <td className={`network-usage-cell ${item.device.network_usage?.warning ? "attention" : ""}`}>
          <strong>{item.device.network_usage?.settings.enabled ? formatNetworkBytes(item.device.network_usage.total_bytes) : "–"}</strong>
          {item.device.network_usage?.settings.enabled && <small>{item.device.network_usage.synchronized ? item.device.network_usage.quota_exceeded ? "Kontingent überschritten" : item.device.network_usage.warning ? "Warnschwelle erreicht" : "Aktueller Zeitraum" : "Warte auf Agent"}</small>}
        </td>
        <td aria-hidden="true" className="device-row-chevron">{expanded ? "⌃" : "⌄"}</td>
      </tr>
      {expanded && <tr className="device-detail-row">
        <td colSpan={8}>
          <section id={detailID} className="device-details-inline" aria-label={`Gerätedetails für ${deviceLabel(item.device)}`}>
            {item.device.active && <ExtensionHealth id={item.device.id} global />}
            <section className="device-detail-activity" aria-label="Geräteaktivität">
              <div className="device-detail-activity-heading">
                <div><span className="device-detail-kicker">Aktivität</span><h4>{latestActivity === "installation" ? "Letzte Installation" : latestActivity === "update" ? "Letztes Agent-Update" : latestActivity === "current" ? "Aktueller Agent" : "Noch kein Vorgang"}</h4></div>
                <div className="device-detail-actions">
                  {item.device.active && !enrollmentForDevice && <button className={setupComplete ? "text-action" : "primary-button compact-primary"} type="button" disabled={busy === `enrollment:${item.device.id}`} onClick={() => void prepareEnrollment(item.device)}>{setupComplete ? "Neu installieren" : "Installationsbefehl erstellen"}</button>}
                  {item.device.active && supportsRemoteUpdates(item.device) && !maintenanceRequired && release && isAgentReleaseNewer(item.device, release) && <button className="text-action" type="button" disabled={Boolean(update.pending) || busy === `update:${item.device.id}`} onClick={() => void updateAgent(item.device)}>{update.retryable ? "Update erneut" : "Aktualisieren"}</button>}
                </div>
              </div>
              {enrollmentForDevice ? <EnrollmentCommand deviceLabel={enrollmentForDevice.deviceLabel} enrollment={enrollmentForDevice.value} onClose={() => setEnrollment(null)} onInstallationChange={(installation) => {
                setDevices((current) => current.map((currentItem) => currentItem.device.id === enrollmentForDevice.deviceID ? { ...currentItem, installation } : currentItem));
              }} /> : latestActivity === "installation" && item.installation ? <InstallationProgress installationID={item.installation.id} initialInstallation={item.installation} onChange={(installation) => {
                setDevices((current) => current.map((currentItem) => currentItem.device.id === installation.device_id ? { ...currentItem, installation } : currentItem));
              }} /> : latestActivity === "update" ? <AgentUpdateProgress device={item.device} release={release} /> : latestActivity === "current" ? <p className="installation-result">Agent {installedAgent} ist installiert. Der frühere Auftrag auf {item.device.agent_update?.desired_version} wurde durch diese neuere Installation ersetzt.</p> : maintenanceRequired ? <p className="installation-result">Vom Pi bestätigt: Agent {installedAgent ?? "unbekannt"}. Server-Release: {release?.version}. Für diesen Versionssprung ist einmalig „Neu installieren“ erforderlich.</p> : <p className="device-detail-note">{installedAgent ? `Agent ${installedAgent} ist gemeldet. Es gibt noch keinen Installations- oder Updatevorgang.` : "Erstelle den Installationsbefehl, um das Gerät einzurichten."}</p>}
            </section>
            {item.device.active && supportsExtensions(item.device) && <section className="device-detail-strip" aria-label="Extensions">
              <div><strong>Extensions</strong><span>Zusatzprogramme für dieses Gerät verwalten.</span></div>
              <button className="text-action" type="button" aria-expanded={extensionsForDevice} onClick={() => {
                setAgentSettingsDeviceID("");
                setExtensionsDevice(extensionsForDevice ? null : item.device);
              }}>{extensionsForDevice ? "Schließen" : "Verwalten"}</button>
              {extensionsForDevice && <DeviceExtensions embedded device={item.device} onClose={() => setExtensionsDevice(null)} onError={setError} onNotice={setNotice} />}
            </section>}
            {item.device.active && <section className="device-detail-strip" aria-label="Agent Settings">
              <div><strong>Agent Settings</strong><span>Heartbeat, Netzwerkzähler, Zeitraum, Kontingent und Warnschwelle verwalten.</span></div>
              <button className="text-action" type="button" aria-label="Agent Settings öffnen" aria-expanded={agentSettingsForDevice} onClick={() => {
                setExtensionsDevice(null);
                setAgentSettingsDeviceID(agentSettingsForDevice ? "" : item.device.id);
              }}>{agentSettingsForDevice ? "Schließen" : "Verwalten"}</button>
              {agentSettingsForDevice && <div className="embedded-agent-settings"><AgentSettings device={item.device} onChanged={(networkUsage) => {
                setDevices((current) => current.map((currentItem) => currentItem.device.id === item.device.id
                  ? { ...currentItem, device: { ...currentItem.device, network_usage: networkUsage } }
                  : currentItem));
              }} onError={setError} onNotice={setNotice} /></div>}
            </section>}
            {item.device.active && <RemoteWebAccess device={item.device} />}
            {item.device.active && <footer className="device-detail-footer">
              <span>Geräteaktionen</span>
              {item.device.status?.online && supportsRemoteRestart(item.device) && <button className="text-action" type="button" disabled={busy === `restart:${item.device.id}`} onClick={() => void restartDevice(item.device)}>Neu starten</button>}
              <button className="danger-link" type="button" disabled={busy === `device:${item.device.id}`} onClick={() => void deactivate(item)}>Außer Betrieb nehmen</button>
            </footer>}
          </section>
        </td>
      </tr>}
    </Fragment>;
  }

  const pageHeading = activeNavigation === "overview"
    ? { title: "Overview", description: "Wichtige Hinweise und aktueller Plattformzustand." }
    : activeNavigation === "management"
    ? { title: "Kunden & Messstellen", description: "Messstellen und ihre dauerhafte Historie verwalten." }
    : activeNavigation === "devices"
      ? { title: "Geräte", description: "RPControl-Geräte, Installation und technische Wartung." }
      : activeNavigation === "backups"
      ? { title: "Einstellungen", description: "Speicherplatz, E-Mail-Warnungen und externe Sicherungen verwalten." }
      : { title: "Extensions", description: "Externe Einreichungen prüfen und signierte Versionen veröffentlichen." };

  return <div className="app-shell">
    <AuthenticatedHeader identity={identity} navigation={platformNavigation} activeNavigation={activeNavigation} onNavigate={navigateSection} onSignOut={signOut} />
    <main className="management-page">
      <div className="dashboard-heading management-heading"><div><h1>{pageHeading.title}</h1><p>{pageHeading.description}</p></div></div>
      {error && <p className="page-error" role="alert">{error}</p>}
      {notice && <p className="page-notice" role="status">{notice}</p>}
      {purgeTenant && <TenantPurgeConfirmation tenant={purgeTenant} busy={busy === `purge:${purgeTenant.id}`} onCancel={() => setPurgeTenant(null)} onConfirm={(confirmation) => permanentlyDeleteTenant(purgeTenant, confirmation)} />}

      {(activeNavigation === "management" || activeNavigation === "devices") && <section className={`management-summary ${activeNavigation}`} aria-label="Plattformstatus">
        {activeNavigation === "management" ? <>
          <article><span>Kunden</span><strong>{tenantSummary.active}</strong><small>{tenantSummary.archived ? `${tenantSummary.archived} archiviert` : "aktive Mandanten"}</small></article>
          <article><span>Messstellen</span><strong>{tenantSummary.points}</strong><small>aktive fachliche Standorte</small></article>
        </> : <>
          <article><span>Aktive Geräte</span><strong>{activeDevices.length}</strong><small>{retiredDevices.length ? `${retiredDevices.length} außer Betrieb` : "globaler Bestand"}</small></article>
          <article><span>Online</span><strong>{deviceSummary.online}</strong><small>aktuell erreichbar</small></article>
          <article><span>Frei</span><strong>{deviceSummary.free}</strong><small>ohne Messstelle</small></article>
        </>}
      </section>}

      <div className="management-sections">
      {activeNavigation === "overview" && <StorageWarnings devices={devices} openStorage={() => navigateSection("backups")} openDevice={id => { navigateSection("devices"); setDetailsDeviceID(id); setAgentSettingsDeviceID(id); }} />}
      {activeNavigation === "overview" && <section className="overview-panel" aria-label="Aktuelle Hinweise">
        <div className="panel-heading device-panel-heading"><div><h2>Aktuelle Hinweise</h2><p>Warnungen, die Aufmerksamkeit benötigen.</p></div><span className={`overview-count ${networkWarnings.length ? "attention" : ""}`}>{networkWarnings.length}</span></div>
        {networkWarnings.length > 0 ? <div className="overview-alerts">{networkWarnings.map((item) => {
          const usage = item.device.network_usage!;
          return <article className="overview-alert" key={item.device.id}>
            <span className="status-marker" />
            <div><strong>{usage.quota_exceeded ? "Netzwerkkontingent überschritten" : "Netzwerk-Warnschwelle erreicht"}</strong><p>{deviceLabel(item.device)}{item.measurement_point ? ` · ${item.tenant} · ${item.measurement_point}` : ""}</p><small>{formatNetworkBytes(usage.total_bytes)} von {formatNetworkBytes(usage.settings.quota_bytes || 0)} verbraucht</small></div>
            <button className="text-action" type="button" onClick={() => { navigateSection("devices"); setDetailsDeviceID(item.device.id); setAgentSettingsDeviceID(item.device.id); }}>Agent Settings öffnen</button>
          </article>;
        })}</div> : <p className="overview-empty">Derzeit hat kein Gerät seine Netzwerk-Warnschwelle erreicht.</p>}
      </section>}
      {activeNavigation === "management" && <div className="management-layout">
        <aside className="customer-panel">
          <div className="panel-heading"><h2>Kunden</h2><button className="text-action" type="button" aria-expanded={showTenantForm} aria-controls="new-customer-form" onClick={() => setShowTenantForm((value) => !value)}>{showTenantForm ? "Schließen" : "Neuer Kunde"}</button></div>
          {showTenantForm && <form id="new-customer-form" className="management-form compact-form" onSubmit={submitTenant}>
            <label>Kundenkennung<input required minLength={3} maxLength={63} value={tenantForm.name} onChange={(event) => setTenantForm({ ...tenantForm, name: event.target.value.toLowerCase() })} /></label>
            <label>Admin-E-Mail<input required type="email" value={tenantForm.email} onChange={(event) => setTenantForm({ ...tenantForm, email: event.target.value })} /></label>
            <label>Initiales Passwort<input required type="password" minLength={12} value={tenantForm.password} onChange={(event) => setTenantForm({ ...tenantForm, password: event.target.value })} /></label>
            <label>Passwort wiederholen<input required type="password" minLength={12} value={tenantForm.confirmation} onChange={(event) => setTenantForm({ ...tenantForm, confirmation: event.target.value })} /></label>
            <button className="primary-button" disabled={busy === "tenant"}>Kunde anlegen</button>
          </form>}
          <div className="customer-list">
            {tenants.map((tenant) => <div className="customer-entry" key={tenant.id}>
              <button type="button" disabled={Boolean(tenant.archived_at)} className={`${tenant.id === selectedTenantID ? "customer-row active" : "customer-row"}${tenant.archived_at ? " archived" : ""}`} onClick={() => setSelectedTenantID(tenant.id)}><span className="customer-avatar">{tenant.name.slice(0, 2).toUpperCase()}</span><span><strong>{tenant.name}</strong><small>{tenant.archived_at ? `Archiviert seit ${new Date(tenant.archived_at).toLocaleDateString(displayLocale)}` : tenant.admin_email}</small></span><b>{tenant.total_device_count}</b></button>
              {!tenant.archived_at && <form method="post" target="_blank" action={`/api/admin/tenants/${tenant.id}/portal/login`}><button className="text-action" type="submit">Kundenportal öffnen ↗</button></form>}<details className="row-menu customer-actions"><summary>Mehr</summary><div>{tenant.archived_at ? <><button className="text-action" type="button" disabled={busy === `restore:${tenant.id}`} onClick={() => void restoreTenant(tenant)}>Dearchivieren</button><button className="danger-link" type="button" onClick={() => setPurgeTenant(tenant)}>Komplett löschen</button></> : <button className="danger-link" type="button" disabled={busy === `archive:${tenant.id}`} onClick={() => void removeTenant(tenant)}>Archivieren</button>}</div></details>
            </div>)}
            {!loading && tenants.length === 0 && <p className="panel-empty">Noch keine Kunden angelegt.</p>}
          </div>
        </aside>

        <div className="management-sections">
        <section className="device-panel">
          <div className="panel-heading device-panel-heading"><div><h2>Messstellen{selectedTenant ? ` · ${selectedTenant.name}` : ""}</h2><p>Die Historie gehört immer zur Messstelle.</p></div>{selectedTenant && <button className="text-action" type="button" aria-expanded={showPointForm} aria-controls="new-measurement-point-form" onClick={() => setShowPointForm((value) => !value)}>{showPointForm ? "Schließen" : "Messstelle hinzufügen"}</button>}</div>
          {showPointForm && selectedTenant && <form id="new-measurement-point-form" className="management-form compact-device-create" onSubmit={submitPoint}><label>Name der Messstelle<input required maxLength={256} placeholder="Hauptstraße · Hauptbahnhof" value={pointName} onChange={(event) => setPointName(event.target.value)} /></label><button className="primary-button" disabled={busy === "point"}>Messstelle anlegen</button></form>}
          <div className="device-table-wrap" aria-busy={pointsLoading}><table className="management-table"><thead><tr><th>Messstelle</th><th>Gerät</th><th>Status</th></tr></thead><tbody>
            {visiblePoints.map((point) => <tr key={point.id}>
              <td><strong>{point.name}</strong></td>
              <td className="assignment-device-cell">
                <div className="assignment-device-control">
                  <select aria-label={`Gerät für ${point.name}`} value={point.assignment?.device.id || ""} disabled={busy === `assign:${point.id}`} onChange={(event) => { const value = event.target.value; if (value && value !== point.assignment?.device.id) void assign(point, value); }}>
                    <option value="" disabled>Kein Gerät · auswählen …</option>
                    {devices.filter((item) => item.device.active || item.device.id === point.assignment?.device.id).map((item) => <option key={item.device.id} value={item.device.id} disabled={item.cleanup_pending}>{deviceLabel(item.device)}{item.device.id === point.assignment?.device.id ? " · aktuell" : item.cleanup_pending ? " · Bereinigung ausstehend" : item.measurement_point ? ` · derzeit ${item.measurement_point}` : " · frei"}</option>)}
                  </select>
                  {point.assignment && <button className="text-action" type="button" disabled={busy === `assign:${point.id}`} onClick={() => void unassign(point)}>Gerät lösen</button>}
                </div>
                <small>{busy === `assign:${point.id}` ? "Zuweisung wird geändert …" : point.assignment ? `Zugewiesen seit ${new Date(point.assignment.starts_at).toLocaleString(displayLocale)}` : "Noch kein Gerät zugewiesen"}</small>
              </td>
              <td><span className={`state-badge ${point.assignment?.device.status?.online ? "online" : point.assignment ? "warning" : "neutral"}`}><i />{point.assignment?.device.status?.online ? "Online" : point.assignment ? "Offline" : "Offen"}</span></td>
            </tr>)}
            {pointsLoading && selectedTenant && <tr><td colSpan={3} className="table-empty table-loading">Messstellen werden geladen …</td></tr>}
            {!pointsLoading && selectedTenant && visiblePoints.length === 0 && <tr><td colSpan={3} className="table-empty">Noch keine Messstellen angelegt.</td></tr>}
          </tbody></table></div>
        </section>

        {selectedTenant && <section className="device-panel measurement-group-panel" aria-labelledby="measurement-group-heading">
          <div className="panel-heading device-panel-heading"><div><h2 id="measurement-group-heading">Messstellenverbünde · {selectedTenant.name}</h2><p>Zwei Messstellen an den Enden einer Engstelle. Die Linienrollen werden im Kundenportal zugeordnet.</p></div>{visiblePoints.length >= 2 && <button className="text-action" type="button" aria-expanded={showGroupForm} aria-controls="new-measurement-group-form" onClick={() => { if (showGroupForm) setGroupFormTenantID(""); else { setGroupForm(emptyGroupForm); setGroupFormTenantID(selectedTenant.id); } }}>{showGroupForm ? "Schließen" : "Verbund anlegen"}</button>}</div>
          {showGroupForm && <form id="new-measurement-group-form" className="management-form group-create-form" onSubmit={submitGroup}>
            <label className="group-create-wide">Name des Verbunds<input required maxLength={120} placeholder="Engstelle Hauptstraße" value={groupForm.name} onChange={(event) => setGroupForm({ ...groupForm, name: event.target.value })} /></label>
            <label>Seite 1<select required value={groupForm.side1} onChange={(event) => setGroupForm({ ...groupForm, side1: event.target.value })}><option value="" disabled>Messstelle auswählen …</option>{visiblePoints.map((point) => <option key={point.id} value={point.id}>{point.name}</option>)}</select></label>
            <label>Seite 2<select required value={groupForm.side2} onChange={(event) => setGroupForm({ ...groupForm, side2: event.target.value })}><option value="" disabled>Messstelle auswählen …</option>{visiblePoints.map((point) => <option key={point.id} value={point.id}>{point.name}</option>)}</select></label>
            <label className="group-create-wide">Zeitzone<input required maxLength={64} value={groupForm.timezone} onChange={(event) => setGroupForm({ ...groupForm, timezone: event.target.value })} /></label>
            <label>Kampagne von<small>optional</small><input type="date" value={groupForm.from} onChange={(event) => setGroupForm({ ...groupForm, from: event.target.value })} /></label>
            <label>Kampagne bis<small>optional</small><input type="date" min={groupForm.from || undefined} value={groupForm.to} onChange={(event) => setGroupForm({ ...groupForm, to: event.target.value })} /></label>
            <p className="group-create-wide group-create-note">Die Seiten sind nach dem Anlegen fest. Zum Tauschen oder Ersetzen einer Messstelle den Verbund löschen und neu anlegen.</p>
            <button className="primary-button" disabled={busy === "group"}>Verbund anlegen</button>
          </form>}
          <div className="device-table-wrap" aria-busy={groupsLoading}><table className="management-table"><thead><tr><th>Verbund</th><th>Seite 1</th><th>Seite 2</th><th>Kampagne</th><th><span className="sr-only">Aktion</span></th></tr></thead><tbody>
            {visibleGroups.map((group) => {
              const side = (value: number) => group.members.find((member) => member.side === value)?.name ?? "–";
              return <tr key={group.id}>
                <td><strong>{group.name}</strong><small>{group.profile === "narrow_passage" ? "Engstelle" : group.profile} · {group.timezone}</small></td>
                <td>{side(1)}</td>
                <td>{side(2)}</td>
                <td>{group.campaign ? `${campaignDate(group.campaign.from_date)} – ${campaignDate(group.campaign.to_date)}` : "Keine"}</td>
                <td><button className="text-action" type="button" aria-expanded={operationsGroupID === group.id} onClick={() => setOperationsGroupID(operationsGroupID === group.id ? "" : group.id)}>Einrichtung und Revision</button><button className="danger-link" type="button" disabled={busy === `group:${group.id}`} onClick={() => void removeGroup(group)}>Löschen</button></td>
              </tr>;
            })}
            {groupsLoading && <tr><td colSpan={5} className="table-empty table-loading">Messstellenverbünde werden geladen …</td></tr>}
            {!groupsLoading && visibleGroups.length === 0 && <tr><td colSpan={5} className="table-empty">{visiblePoints.length >= 2 || pointsLoading ? "Noch keine Messstellenverbünde angelegt." : "Für einen Verbund braucht der Kunde mindestens zwei Messstellen."}</td></tr>}
          </tbody></table></div>
          {visibleGroups.filter(group => group.id === operationsGroupID).map(group => <NarrowPassageOperations key={`${selectedTenant.id}:${group.id}`} tenantID={selectedTenant.id} group={group} points={visiblePoints} />)}
        </section>}
        </div>
      </div>}

      {activeNavigation === "devices" && <>
      <details className="device-panel archived-measurements-panel">
        <summary>Nicht zugeordnete Messdaten ({unassignedGroups.length === 100 ? "100+" : unassignedGroups.length})</summary>
        <div className="archived-measurements-content">
          <div className="archive-toolbar"><p>Nur gültige Messungen bekannter Geräte. Sie werden erst nach deiner Zuordnung für den gewählten Kunden sichtbar.</p><button className="text-action" type="button" onClick={() => { setUnassignedGroups([]); setArchiveLoaded(false); }}>Aktualisieren</button></div>
          <div className="device-table-wrap"><table className="management-table"><thead><tr><th>Gerät</th><th>Messzeitraum</th><th>Umfang</th><th>Messstelle</th></tr></thead><tbody>
            {unassignedGroups.map((group) => {
              const key = `${group.device_id}:${group.claimed_assignment_id}`;
              return <tr key={key}>
                <td><strong>{group.serial_number || group.device_cn}</strong></td>
                <td>{new Date(group.first_event_at).toLocaleString(displayLocale)}<small>bis {new Date(group.last_event_at).toLocaleString(displayLocale)}</small></td>
                <td><span>{group.record_count.toLocaleString(displayLocale)} Datensätze</span><small>{group.vehicle_count.toLocaleString(displayLocale)} Fahrzeuge</small></td>
                <td><div className="archive-assignment">
                  <select aria-label={`Messstelle für Messdaten von ${group.serial_number || group.device_cn}`} value={archiveTargets[key] || ""} onChange={(event) => setArchiveTargets((current) => ({ ...current, [key]: event.target.value }))}>
                    <option value="">Messstelle auswählen …</option>
                    {archivePoints.map((point) => <option key={point.id} value={point.id}>{point.tenant} · {point.name}</option>)}
                  </select>
                  <button className="text-action" type="button" disabled={!archiveTargets[key] || busy === `archive:${key}`} onClick={() => void resolveArchiveGroup(group)}>Zuordnen</button>
                </div></td>
              </tr>;
            })}
            {archiveLoaded && unassignedGroups.length === 0 && <tr><td colSpan={4} className="table-empty">Keine offenen Messdaten vorhanden.</td></tr>}
            {!archiveLoaded && <tr><td colSpan={4} className="table-empty table-loading">Messdaten werden geladen …</td></tr>}
          </tbody></table></div>
        </div>
      </details>

      <section className="device-panel global-device-panel">
        {deviceRefreshFailed && <p className="form-error" role="alert">Gerätestatus konnte nicht aktualisiert werden. Angezeigt wird der letzte bekannte Stand; die Verbindung wird automatisch erneut geprüft.</p>}
        <div className="panel-heading device-panel-heading"><div><h2>Globale RPControl-Geräte</h2><p>Kundenunabhängige Seriennummern, Zertifikate und technischer Zustand.</p></div><button className="text-action" type="button" disabled={busy === "device"} onClick={() => void addDevice()}>Neues Gerät</button></div>
        <div className="device-table-wrap"><table className="management-table"><thead><tr><th>Gerät</th><th>Aktuelle Zuweisung</th><th>Status</th><th>Lokale IP</th><th>Speicher frei</th><th>SQL-Puffer</th><th>Verbrauch</th><th></th></tr></thead><tbody>
          {activeDevices.map(renderDeviceRow)}
          {!loading && activeDevices.length === 0 && <tr><td colSpan={8} className="table-empty">Keine aktiven Geräte vorhanden.</td></tr>}
        </tbody></table></div>
        {retiredDevices.length > 0 && <details className="retired-devices">
          <summary>Geräte außer Betrieb anzeigen ({retiredDevices.length})</summary>
          <div className="device-table-wrap"><table className="management-table"><thead><tr><th>Gerät</th><th>Zuweisung</th><th>Status</th><th>Lokale IP</th><th>Speicher frei</th><th>SQL-Puffer</th><th>Verbrauch</th><th></th></tr></thead><tbody>
            {retiredDevices.map(renderDeviceRow)}
          </tbody></table></div>
        </details>}
      </section>
      </>}
      {activeNavigation === "extensions" && <ExtensionAdministration onUnauthorized={onSignedOut} onError={setError} onNotice={setNotice} />}
      {activeNavigation === "backups" && <BackupAdministration onUnauthorized={onSignedOut} />}
      </div>
    </main>
  </div>;
}
